import { useTranslation } from 'next-i18next'
import { useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Divider from '~/bootcamp/Activities/components/Divider'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'

import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useFractionSimplifyDenom from './useFractionSimplifyDenom'

function FractionSimplifyDenom({
  answerState,
  actionButtonsFC,
  autofocus = true,
  difficultyLevel,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
}: ActivityProps): React.ReactElement {
  const {
    question: { leftNum, leftDenom, rightDenom },
    answer,
    formValid,
    error,
    onInputChange,
    handleCheckAnswer,
    goToNextQuestion,
  } = useFractionSimplifyDenom({
    difficultyLevel,
    onAnswerStateChange,
    onNextQuestionClick,
  })
  const { t } = useTranslation(['bootcamp', 'generics'])
  const [inputKey, setInputKey] = useState<string>(uuid())
  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  // `divisor` stays out: it IS the answer both factor slots check against.
  useEmitInstance(
    onInstanceChange,
    leftDenom
      ? `\\frac{${leftNum} \\div \\square}{${leftDenom} \\div \\square} = \\frac{\\square}{${rightDenom}}`
      : null,
  )

  const handleGoToNextQuestion = (): void => {
    goToNextQuestion()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    hocOnEnterPress(answerState, handleCheckAnswer, handleGoToNextQuestion)
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper>
        <div className="flex items-center space-x-1">
          <div>
            <span className="flex items-center space-x-1 justify-end">
              <span className="text-2xl h-13.5 flex items-end">
                <span>{leftNum}</span>
                <span>{t('bootcamp:signs.division')}</span>
              </span>
              <Input
                aria-label={t('generics:ariaLabelButtons.insertValue')}
                autoFocus={autofocus}
                isError={error && error.leftNumFactor}
                key={`${inputKey}-first`}
                maxDigits={3}
                onChange={(value) => onInputChange('leftNumFactor', value)}
                onEnter={onEnterPress}
                ref={inputRef}
                value={`${answer.leftNumFactor || ''}`}
              />
            </span>
            <Divider className="my-2" />
            <span className="flex items-center space-x-1 justify-end">
              <span className="text-2xl h-13.5 flex items-start">
                <span>{leftDenom}</span>
                <span>{t('bootcamp:signs.division')}</span>
              </span>
              <Input
                aria-label={t('generics:ariaLabelButtons.insertValue')}
                isError={error && error.leftDenomFactor}
                key={`${inputKey}-second`}
                maxDigits={3}
                onChange={(value) => onInputChange('leftDenomFactor', value)}
                onEnter={onEnterPress}
                value={`${answer.leftDenomFactor || ''}`}
              />
            </span>
          </div>
          <Equals />
          <div className="flex flex-col items-center">
            <Input
              aria-label={t('generics:ariaLabelButtons.insertNumerator')}
              isError={error && error.rightNum}
              key={`${inputKey}-third`}
              maxDigits={3}
              onChange={(value) => onInputChange('rightNum', value)}
              onEnter={onEnterPress}
              value={`${answer.rightNum || ''}`}
            />
            <Divider className="my-2" />
            <span className="text-2xl h-10 sm:h-13.5 flex items-start">
              <span>{rightDenom}</span>
            </span>
          </div>
        </div>
      </FractionsActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleCheckAnswer,
          clearFormAction: () => onInputChange(),
          goToNextQuestionAction: handleGoToNextQuestion,
        })}
    </ActivityWrapper>
  )
}

export default FractionSimplifyDenom
